import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { OPS } from "@/constants/testIds";
import { toLatinDigits } from "@/lib/wmDigits";
import { DK_TIMEOUT_MS, dkError, dkMessage, fmt, fmtQuantity, normKey, sanitizeQuantity, unitOf } from "./dkUtils";

const I = OPS.dk.items;
const FIELDS = ["name_ar", "name_en", "pack_unit", "pack_size", "recipe_ingredient_keys", "flag_min_milli",
    "excluded_location_codes", "sort_order", "is_active"];

const str = (v) => (v === null || v === undefined ? "" : String(v));
const fold = (s) => String(s || "").toLowerCase()
    .replace(/[أإآ]/g, "ا").replace(/ى/g, "ي").replace(/ة/g, "ه").replace(/ـ/g, "");

function fromItem(item) {
    return {
        name_ar: str(item?.name_ar),
        name_en: str(item?.name_en),
        pack_unit: str(item?.pack_unit),
        pack_size: str(item?.pack_size),
        recipe_ingredient_keys: Array.isArray(item?.recipe_ingredient_keys) ? item.recipe_ingredient_keys : [],
        flag_min: item?.flag_min_milli === null || item?.flag_min_milli === undefined ? "" : String(item.flag_min_milli / 1000),
        excluded_location_codes: Array.isArray(item?.excluded_location_codes) ? item.excluded_location_codes : [],
        sort_order: str(item?.sort_order),
        is_active: item?.is_active !== false,
    };
}

/** Form -> the PATCH fields (SPEC §3.4). Blank optional text -> null. */
function toPayload(f) {
    const s = (v) => str(v).trim();
    const n = (v) => (s(v) === "" || s(v) === "." ? null : Number(s(v)));
    const packUnit = s(f.pack_unit) || null;
    const min = n(f.flag_min);
    const sort = s(f.sort_order);
    return {
        name_ar: s(f.name_ar),
        name_en: s(f.name_en),
        pack_unit: packUnit,
        pack_size: packUnit ? n(f.pack_size) : null,
        recipe_ingredient_keys: [...f.recipe_ingredient_keys],
        flag_min_milli: min === null ? null : Math.round(min * 1000),
        excluded_location_codes: [...f.excluded_location_codes].sort(),
        sort_order: sort === "" || sort === "-" ? null : parseInt(sort, 10),
        is_active: !!f.is_active,
    };
}

function validate(p) {
    const e = {};
    if (!p.name_ar) e.name_ar = "dk_items_err_required";
    if (p.pack_unit && !(p.pack_size > 0)) e.pack_size = "dk_items_err_pack_size";
    if (p.flag_min_milli !== null && !(p.flag_min_milli >= 0)) e.flag_min_milli = "dk_items_err_min";
    return e;
}

function Field({ k, label, required, error, hint, children, className = "" }) {
    return (
        <div className={`min-w-0 space-y-1 ${className}`}>
            <Label className="text-xs font-bold text-[color:var(--ink)]">
                {label}{required ? <span className="text-[color:var(--danger)]"> *</span> : null}
            </Label>
            {children}
            {hint ? <p className="text-[11px] leading-snug text-[color:var(--ink-faint)]">{hint}</p> : null}
            {error ? <p data-field-error={k} className="text-[11px] font-semibold text-[color:var(--danger)]">{error}</p> : null}
        </div>
    );
}

/** Group `/dk/ingredient-keys` rows by key: [{key, units:[...], recipes_n}]. */
export function groupKeys(rows) {
    const m = new Map();
    for (const r of rows || []) {
        const key = r?.ingredient_key;
        if (!key) continue;
        const g = m.get(key) || { key, units: [], recipes_n: 0 };
        if (r.unit && !g.units.includes(r.unit)) g.units.push(r.unit);
        g.recipes_n += Number(r.recipes_n || 0);
        m.set(key, g);
    }
    return [...m.values()].sort((a, b) => b.recipes_n - a.recipes_n || a.key.localeCompare(b.key, "ar"));
}

/**
 * Edit one daily-count item (reviewers): names, the pack helper, the recipe
 * ingredient keys behind «حسب المبيعات» (a multi-select from `/dk/ingredient-keys`,
 * never with costs), the per-item flag minimum, the excluded locations and
 * active. The unit class is fixed here (the server locks it once used). A PATCH
 * sends only the changed keys; a server field error shows under its input.
 */
export function ItemDialog({ item, scope, keys, keysLoaded, onClose, onSaved }) {
    const { t, tn, lang } = useI18n();
    const [form, setForm] = useState(() => fromItem(item));
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const [kq, setKq] = useState("");
    const unit = unitOf(item, t);
    const set = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        const ek = k === "flag_min" ? "flag_min_milli" : k;
        setErrors((e) => { if (!e[ek]) return e; const n = { ...e }; delete n[ek]; return n; });
    };
    const toggle = (k, v, on) => set(k, on ? [...new Set([...form[k], v])] : form[k].filter((x) => x !== v));
    // Recipe keys match the way the server matches them (`_norm_key`): a stored key that
    // differs from the recipe book's only in case / spacing is that option, ticked.
    const toggleKey = (v, on) => {
        const n = normKey(v);
        const rest = form.recipe_ingredient_keys.filter((x) => normKey(x) !== n);
        set("recipe_ingredient_keys", on ? [...rest, v] : rest);
    };
    const invalid = (k) => (errors[k] ? "border-[color:var(--danger)]" : "");

    const known = useMemo(() => new Set((keys || []).map((g) => normKey(g.key))), [keys]);
    const selected = useMemo(() => new Set(form.recipe_ingredient_keys.map(normKey)), [form.recipe_ingredient_keys]);
    const shownKeys = useMemo(() => {
        const s = fold(kq.trim());
        const list = (keys || []).filter((g) => !s || fold(g.key).includes(s));
        return [...list.filter((g) => selected.has(normKey(g.key))), ...list.filter((g) => !selected.has(normKey(g.key)))];
    }, [keys, kq, selected]);
    const brokenKeys = keysLoaded ? form.recipe_ingredient_keys.filter((k) => !known.has(normKey(k))) : [];

    const locs = useMemo(() => {
        const list = (scope?.locations || []).filter((l) => l.code);
        const codes = new Set(list.map((l) => l.code));
        const extra = form.excluded_location_codes.filter((c) => !codes.has(c)).map((c) => ({ id: c, code: c }));
        return [...list, ...extra];
    }, [scope, form.excluded_location_codes]);

    const defMin = (item?.unit_class === "pc" ? scope?.settings?.flag_min_pc_milli : scope?.settings?.flag_min_kg_milli);

    const save = async () => {
        const payload = toPayload(form);
        const local = validate(payload);
        if (Object.keys(local).length) {
            const m = {};
            Object.entries(local).forEach(([k, key]) => { m[k] = t(key); });
            setErrors(m);
            toast.error(t("dk_items_fix_fields"));
            return;
        }
        const before = toPayload(fromItem(item));
        const body = {};
        Object.keys(payload).forEach((k) => {
            if (JSON.stringify(payload[k]) !== JSON.stringify(before[k])) body[k] = payload[k];
        });
        if ("sort_order" in body && (body.sort_order === null || Number.isNaN(body.sort_order))) delete body.sort_order;
        // a pack and its size are validated together: send both when either changed
        if ("pack_unit" in body || "pack_size" in body) { body.pack_unit = payload.pack_unit; body.pack_size = payload.pack_size; }
        if (!Object.keys(body).length) { toast(t("dk_items_no_changes"), { id: "dk-item-no-changes" }); onClose(); return; }
        setBusy(true);
        setErrors({});
        try {
            const r = await api.patch(`/dk/items/${item.id}`, body, { timeout: DK_TIMEOUT_MS });
            toast.success(t("dk_items_saved"));
            onSaved && (await onSaved(r.data));
        } catch (e) {
            const d = dkError(e);
            if (d && FIELDS.includes(d.field)) setErrors({ [d.field]: lang === "ar" ? d.message_ar : d.message_en });
            toast.error(dkMessage(e, lang) || t("dk_offline_error"));
        } finally {
            setBusy(false);
        }
    };

    const input = "h-11 text-base";
    return (
        <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
            <DialogContent data-testid={I.dialog} className="max-h-[88vh] max-w-lg overflow-y-auto bg-[color:var(--bg-surface)] scrollbar-thin">
                <DialogHeader className="text-start sm:text-start">
                    <DialogTitle>{t("dk_items_edit")} — {tn(item)}</DialogTitle>
                    <DialogDescription>
                        {t(item?.unit_class === "pc" ? "dk_unit_class_pc" : "dk_unit_class_kg")}
                        {item?.sc_item_code ? <> · <bdi dir="ltr">{item.sc_item_code}</bdi></> : null}
                        {item?.sc_item_code && !item?.sc_item_id ? <> · {t("dk_items_sc_unlinked")}</> : null}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <section className="space-y-3">
                        <Field k="name_ar" label={t("dk_items_name_ar")} required error={errors.name_ar}>
                            <Input data-testid={I.field("name_ar")} value={form.name_ar} dir="rtl" maxLength={60}
                                   onChange={(e) => set("name_ar", e.target.value)} className={`${input} ${invalid("name_ar")}`} />
                        </Field>
                        <Field k="name_en" label={t("dk_items_name_en")} error={errors.name_en}>
                            <Input data-testid={I.field("name_en")} value={form.name_en} dir="ltr" maxLength={60}
                                   onChange={(e) => set("name_en", e.target.value)} className={`${input} ${invalid("name_en")}`} />
                        </Field>
                    </section>

                    <section className="space-y-3 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] p-3">
                        <h4 className="text-xs font-bold text-[color:var(--ink-muted)]">{t("dk_items_section_pack")}</h4>
                        <div className="grid grid-cols-2 gap-3">
                            <Field k="pack_unit" label={t("dk_items_pack_unit")} error={errors.pack_unit}>
                                <Input data-testid={I.field("pack_unit")} value={form.pack_unit} maxLength={40}
                                       placeholder={t("dk_items_pack_unit_ph")}
                                       onChange={(e) => set("pack_unit", e.target.value)} className={`${input} ${invalid("pack_unit")}`} />
                            </Field>
                            <Field k="pack_size" label={t("dk_items_pack_size")} required={!!form.pack_unit.trim()} error={errors.pack_size}>
                                <div className="relative" dir="ltr">
                                    <Input data-testid={I.field("pack_size")} value={form.pack_size} dir="ltr" inputMode="decimal"
                                           disabled={!form.pack_unit.trim()} autoComplete="off"
                                           onChange={(e) => set("pack_size", sanitizeQuantity(e.target.value, item?.unit_class !== "pc"))}
                                           className={`${input} pe-14 tabular-nums ${invalid("pack_size")}`} />
                                    <span className="pointer-events-none absolute inset-y-0 end-3 flex max-w-[3rem] items-center truncate text-xs font-medium text-[color:var(--ink-muted)]">
                                        {unit}
                                    </span>
                                </div>
                            </Field>
                        </div>
                        <p className="text-[11px] leading-relaxed text-[color:var(--ink-faint)]">{t("dk_items_pack_hint")}</p>
                    </section>

                    <section className="space-y-2 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] p-3">
                        <div className="flex items-center justify-between gap-2">
                            <h4 className="text-xs font-bold text-[color:var(--ink-muted)]">{t("dk_items_recipe_keys")}</h4>
                            <Badge variant="secondary" className="px-2 py-0 text-[10px]">
                                <bdi dir="ltr">{fmtQuantity(form.recipe_ingredient_keys.length, lang)}</bdi>
                            </Badge>
                        </div>
                        <p className="text-[11px] leading-relaxed text-[color:var(--ink-faint)]">{t("dk_items_recipe_keys_hint")}</p>
                        {brokenKeys.length ? (
                            <div className="space-y-1 rounded-[var(--radius-sm)] bg-[color:var(--warning-soft)] px-2.5 py-2">
                                <p className="text-[11px] font-semibold text-[color:var(--warning-soft-fg)]">{t("dk_items_key_broken")}</p>
                                <div className="flex flex-wrap gap-1">
                                    {brokenKeys.map((k) => (
                                        <button key={k} type="button" onClick={() => toggleKey(k, false)}
                                                className="rounded-full border border-[color:var(--warning-soft-fg)]/40 px-2 py-0.5 text-[11px] text-[color:var(--warning-soft-fg)]">
                                            {k} ×
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                        <div className="relative">
                            <Search className="pointer-events-none absolute start-2.5 top-3 h-4 w-4 text-[color:var(--ink-faint)]" />
                            <Input data-testid={I.keySearch} value={kq} onChange={(e) => setKq(e.target.value)} type="search" autoComplete="off"
                                   placeholder={t("dk_items_key_search")} className="h-10 w-full ps-8 text-base" />
                        </div>
                        {!keysLoaded ? (
                            <p className="py-3 text-center text-[11px] text-[color:var(--ink-faint)]">{t("dk_items_keys_unavailable")}</p>
                        ) : (
                            <ul data-testid={I.keys} className="max-h-56 space-y-1 overflow-y-auto pe-1 scrollbar-thin">
                                {shownKeys.length ? shownKeys.map((g) => {
                                    const on = selected.has(normKey(g.key));
                                    return (
                                        <li key={g.key}>
                                            <label data-testid={I.keyOption(g.key)} data-checked={on ? "true" : "false"}
                                                   className={`flex cursor-pointer items-start gap-2 rounded-[var(--radius-sm)] border px-2.5 py-2 ${
                                                       on ? "border-[color:var(--brand-orange)] bg-[color:var(--brand-orange-soft)]" : "border-[color:var(--border-soft)]"
                                                   }`}>
                                                <Checkbox checked={on} onCheckedChange={(v) => toggleKey(g.key, !!v)} className="mt-0.5" />
                                                <span className="min-w-0 flex-1">
                                                    <span className="block break-words text-xs font-semibold text-[color:var(--ink)]">{g.key}</span>
                                                    <span className="block text-[10px] text-[color:var(--ink-faint)]">
                                                        {g.units.join(" · ")}
                                                        {" · "}{fmt(t("dk_items_recipes_n"), { n: fmtQuantity(g.recipes_n, lang) })}
                                                    </span>
                                                </span>
                                            </label>
                                        </li>
                                    );
                                }) : <li className="py-3 text-center text-[11px] text-[color:var(--ink-faint)]">{t("dk_items_no_keys")}</li>}
                            </ul>
                        )}
                    </section>

                    <section className="space-y-3 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] p-3">
                        <Field k="flag_min_milli" label={t("dk_items_flag_min")} error={errors.flag_min_milli}
                               hint={fmt(t("dk_items_flag_min_hint"), { n: defMin === null || defMin === undefined ? "—" : fmtQuantity(defMin / 1000, lang), unit })}>
                            <div className="relative" dir="ltr">
                                <Input data-testid={I.field("flag_min")} value={form.flag_min} dir="ltr" inputMode="decimal" autoComplete="off"
                                       placeholder={defMin === null || defMin === undefined ? "" : String(defMin / 1000)}
                                       onChange={(e) => set("flag_min", sanitizeQuantity(e.target.value, item?.unit_class !== "pc"))}
                                       className={`${input} pe-14 tabular-nums ${invalid("flag_min_milli")}`} />
                                <span className="pointer-events-none absolute inset-y-0 end-3 flex max-w-[3rem] items-center truncate text-xs font-medium text-[color:var(--ink-muted)]">
                                    {unit}
                                </span>
                            </div>
                        </Field>
                        <Field k="excluded_location_codes" label={t("dk_items_excluded")} error={errors.excluded_location_codes}
                               hint={t("dk_items_excluded_hint")}>
                            <div className="grid grid-cols-2 gap-1.5">
                                {locs.map((l) => {
                                    const on = form.excluded_location_codes.includes(l.code);
                                    return (
                                        <label key={l.code} data-testid={I.excluded(l.code)} data-checked={on ? "true" : "false"}
                                               className="flex min-w-0 cursor-pointer items-center gap-2 rounded-[var(--radius-sm)] border border-[color:var(--border-soft)] px-2.5 py-2">
                                            <Checkbox checked={on} onCheckedChange={(v) => toggle("excluded_location_codes", l.code, !!v)} />
                                            <span className="truncate text-xs text-[color:var(--ink)]">{l.name_ar || l.name_en ? tn(l) : <bdi dir="ltr">{l.code}</bdi>}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </Field>
                    </section>

                    <section className="grid grid-cols-2 gap-3">
                        <Field k="sort_order" label={t("dk_items_sort_order")} error={errors.sort_order}>
                            <Input data-testid={I.field("sort_order")} value={form.sort_order} dir="ltr" inputMode="numeric" autoComplete="off"
                                   onChange={(e) => set("sort_order", toLatinDigits(e.target.value).replace(/(?!^-)[^\d]/g, ""))}
                                   className={`${input} tabular-nums ${invalid("sort_order")}`} />
                        </Field>
                        <Field k="is_active" label={t("dk_items_active")} error={errors.is_active}>
                            <label className="flex h-11 items-center gap-2">
                                <Switch data-testid={I.field("is_active")} checked={form.is_active}
                                        onCheckedChange={(v) => set("is_active", !!v)} />
                                <span className="text-xs text-[color:var(--ink-muted)]">{t(form.is_active ? "dk_active" : "dk_inactive")}</span>
                            </label>
                        </Field>
                    </section>
                </div>

                <DialogFooter className="gap-2 sm:gap-2">
                    <Button type="button" variant="outline" data-testid={I.cancel} disabled={busy} onClick={onClose}>{t("dk_cancel")}</Button>
                    <Button type="button" data-testid={I.save} disabled={busy} onClick={save}>
                        {busy ? t("dk_saving") : t("dk_save")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
